import { z } from "zod";
import { dateTimeSchema, enforceSameTenant, enforceVisibilityInvariant, nonEmptyStringSchema, tenantOwnedShape, } from "./common.js";
import { connectionIdSchema, memberIdSchema } from "./ids.js";
import { processingJobTypeSchema } from "./processing.js";
import { providerSchema, visibilityClassSchema } from "./vocabulary.js";
export const controlPlaneStateSchema = z.enum([
    "empty",
    "scope_required",
    "syncing",
    "healthy",
    "partial",
    "failed",
    "reauthorization_required",
]);
export const controlPlaneCoverageMetricSchema = z
    .object({
    completed: z.number().int().nonnegative(),
    total: z.number().int().nonnegative().nullable(),
    partial: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    unsupported: z.number().int().nonnegative(),
})
    .strict()
    .superRefine((value, context) => {
    const accounted = value.completed + value.partial + value.failed + value.unsupported;
    if (value.total !== null && accounted > value.total) {
        context.addIssue({
            code: "custom",
            path: ["total"],
            message: "Terminal coverage counts cannot exceed total.",
        });
    }
});
export const controlPlaneCoverageSchema = z
    .object({
    sources: controlPlaneCoverageMetricSchema,
    attachments: controlPlaneCoverageMetricSchema,
    text: controlPlaneCoverageMetricSchema,
    graph: controlPlaneCoverageMetricSchema,
    watermark: dateTimeSchema.nullable(),
    historyComplete: z.boolean(),
})
    .strict();
export const controlPlaneHistoryEstimateKindSchema = z.enum([
    "exact",
    "upper_bound",
]);
export const controlPlaneInventoryModeSchema = z.enum([
    "initial",
    "incremental",
]);
export const controlPlaneHistoryEstimateSchema = z
    .object({
    historyStartAt: dateTimeSchema.nullable(),
    total: z.number().int().nonnegative(),
    kind: controlPlaneHistoryEstimateKindSchema,
    calculatedAt: dateTimeSchema,
    cached: z.boolean(),
})
    .strict();
export const controlPlaneSyncProgressSchema = z
    .object({
    phase: z.enum(["analyzing", "inventorying", "processing", "complete"]),
    inventoryMode: controlPlaneInventoryModeSchema.nullable(),
    startedAt: dateTimeSchema.nullable(),
    historyStartAt: dateTimeSchema.nullable(),
    estimatedItems: z.number().int().nonnegative().nullable(),
    estimateKind: controlPlaneHistoryEstimateKindSchema.nullable(),
    estimatedAt: dateTimeSchema.nullable(),
    processedItems: z.number().int().nonnegative(),
})
    .strict();
/**
 * A provider source kind. A Microsoft connection can expose mail, calendar,
 * Teams chat, OneDrive files and selected SharePoint libraries; each is
 * authorized by its own granted scope. WhatsApp and other providers append here
 * as their connectors ship — never before.
 */
export const controlPlaneSourceKindSchema = z.enum([
    "mail",
    "calendar",
    "teams",
    "onedrive",
    "sharepoint",
]);
/**
 * How much work a single source still has in the processing queue: jobs waiting
 * (`queued`), jobs a worker holds right now (`inProgress`) and jobs that ended
 * terminally without a result (`failed`). Counts only — never an error, an
 * identifier or an ETA. Zero is an honest "nothing outstanding"; the whole
 * object is absent when the backlog could not be read at all.
 */
export const controlPlaneSourceProcessingSchema = z
    .object({
    queued: z.number().int().nonnegative(),
    inProgress: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
})
    .strict();
/**
 * The honest per-source-kind status a connection can expose. `granted` is
 * derived fail-closed from the connection's granted scope set: false when the
 * scope is absent. `watermark` is the per-kind coverage cut-off; it is null when
 * no per-container coverage watermark is available on the read surface (the
 * aggregate connection coverage watermark is carried separately on the summary).
 * A null watermark is never a fabricated value — it is an explicit "not
 * established".
 */
export const controlPlaneSourceTypeSchema = z
    .object({
    kind: controlPlaneSourceKindSchema,
    granted: z.boolean(),
    watermark: dateTimeSchema.nullable(),
    // Additive resource-selection state. Omitted means the serving repository
    // has no configured-resource read surface yet. SharePoint can use these to
    // distinguish a granted Sites.Read.All scope from an actually selected
    // library without changing legacy contract-3 payloads.
    configured: z.boolean().optional(),
    resourceCount: z.number().int().nonnegative().optional(),
    // Additive per-service switch: the owner can disable an authorized service
    // so no new automatic work is produced for it, while its history is kept and
    // the other services keep running. Omitted means the read surface does not
    // expose the switch (an honest unknown); a present `false` is an explicit
    // owner-disabled state. `granted` and `enabled` are independent — effective
    // work needs both — so a disabled but still-granted service reads
    // `granted: true, enabled: false`.
    enabled: z.boolean().optional(),
    // Additive backlog state, so the panel can show what is being processed
    // inside the source row it belongs to instead of in a separate card.
    // Omitted means the backlog read surface reported nothing — an honest
    // unknown, never a fabricated zero.
    processing: controlPlaneSourceProcessingSchema.optional(),
})
    .strict();
export const controlPlaneConnectionSchema = z
    .object({
    ...tenantOwnedShape,
    id: connectionIdSchema,
    provider: providerSchema,
    accountLabel: nonEmptyStringSchema.max(320),
    visibilityClass: visibilityClassSchema.nullable(),
    ownerMemberId: memberIdSchema.nullable(),
    historyStartAt: dateTimeSchema.nullable(),
    lastSuccessfulSyncAt: dateTimeSchema.nullable(),
    lastAttemptAt: dateTimeSchema.nullable(),
    // Additive, optional: a contract 2 connection without this field stays
    // byte-for-byte valid. Contract 3 populates it so the connector grid can
    // draw honest per-source-kind cards from a single connection.
    sourceTypes: z.array(controlPlaneSourceTypeSchema).max(16).optional(),
    // Work that belongs to this connection but to no single source card:
    // connection-wide pipeline stages, and anything the read surface could not
    // resolve to a source. Carried so the per-source counts above can never
    // silently lose part of the connection's backlog. Contract 3 only.
    processingUnattributed: controlPlaneSourceProcessingSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.visibilityClass !== null) {
        enforceVisibilityInvariant({
            tenantId: value.tenantId,
            visibility: value.visibilityClass,
            ownerMemberId: value.ownerMemberId,
        }, context);
    }
    if (value.sourceTypes) {
        const seen = new Set();
        value.sourceTypes.forEach((sourceType, index) => {
            if (seen.has(sourceType.kind)) {
                context.addIssue({
                    code: "custom",
                    path: ["sourceTypes", index, "kind"],
                    message: "A source kind cannot appear more than once.",
                });
            }
            seen.add(sourceType.kind);
        });
    }
});
/**
 * Every attention item the control plane can raise, by name.
 *
 * The identifier is the contract, not the sentence beside it. `title` and
 * `detail` are English strings composed on the server, so a surface that
 * rendered them printed English into a Turkish product and could never be
 * translated without redeploying the API. Naming the cases here moves the
 * wording to whichever surface is doing the reading, and makes adding a seventh
 * case a compile error in every catalogue that has to answer for it rather than
 * a sentence somebody notices on a screen months later.
 */
export const controlPlaneIssueIds = [
    "microsoft_sync_not_started",
    "microsoft_reauthorization_required",
    "microsoft_sync_stalled",
    "filtered_delta_result_limit_reached",
    "microsoft_sync_failed",
    "coverage_not_reconciled",
];
export const controlPlaneIssueIdSchema = z.enum(controlPlaneIssueIds);
export const controlPlaneIssueSchema = z
    .object({
    id: controlPlaneIssueIdSchema,
    // Retained because the payload is read by more than the web surface, and
    // dropping a field is a breaking change for consumers this package does not
    // own. Presentation surfaces resolve their own wording from `id`.
    title: nonEmptyStringSchema.max(500),
    detail: nonEmptyStringSchema.max(4000),
    retryable: z.boolean(),
})
    .strict();
export function isControlPlaneCoverageReconciled(value) {
    const metrics = [value.sources, value.attachments, value.text, value.graph];
    return (value.historyComplete &&
        value.watermark !== null &&
        metrics.every((metric) => metric.total !== null &&
            metric.completed +
                metric.partial +
                metric.failed +
                metric.unsupported ===
                metric.total &&
            metric.partial === 0 &&
            metric.failed === 0));
}
export function isControlPlaneSourceSyncComplete(value) {
    return (value.historyComplete &&
        value.sources.total !== null &&
        value.sources.completed === value.sources.total &&
        value.sources.partial === 0 &&
        value.sources.failed === 0 &&
        value.sources.unsupported === 0);
}
export const controlPlaneSummarySchema = z
    .object({
    ...tenantOwnedShape,
    state: controlPlaneStateSchema,
    tenantName: nonEmptyStringSchema.max(300).nullable(),
    memberId: memberIdSchema,
    memberName: nonEmptyStringSchema.max(300).nullable(),
    connection: controlPlaneConnectionSchema.nullable(),
    coverage: controlPlaneCoverageSchema,
    syncProgress: controlPlaneSyncProgressSchema.nullable().optional(),
    issues: z.array(controlPlaneIssueSchema).max(1000),
    updatedAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.connection) {
        enforceSameTenant(value.tenantId, [{ tenantId: value.connection.tenantId, path: ["connection"] }], context);
    }
    if (value.state === "empty" && value.connection !== null) {
        context.addIssue({
            code: "custom",
            path: ["connection"],
            message: "Empty control-plane state cannot contain a connection.",
        });
    }
    if (value.state !== "empty" && value.connection === null) {
        context.addIssue({
            code: "custom",
            path: ["connection"],
            message: "Connected control-plane states require a connection.",
        });
    }
    if (value.state === "scope_required" &&
        value.connection?.visibilityClass !== null) {
        context.addIssue({
            code: "custom",
            path: ["connection", "visibilityClass"],
            message: "scope_required requires an unresolved visibility choice.",
        });
    }
    if (value.state === "healthy" &&
        !isControlPlaneSourceSyncComplete(value.coverage)) {
        context.addIssue({
            code: "custom",
            path: ["coverage"],
            message: "Healthy state requires a completed source synchronization.",
        });
    }
});
/**
 * The pipeline stages a processing job can occupy. These are the worker queue's
 * own job types, surfaced so the sync-control panel can show, per stage, how
 * much work is queued and in flight. The internal stage name is a stable
 * contract value; the UI maps it to a human label.
 */
// The backlog view reports whatever the queue actually contains, so this must
// cover every job type — a missing member is not a cosmetic gap, it fails Zod
// validation for the whole summary and the panel shows "Control plane
// unavailable". `media_understanding` was absent from both this list and
// `processingJobTypeSchema` because no media job had ever existed; the
// attachment completeness fix in 3ff573c created the first ones and the omission
// became a live 400 the same day. Deriving from the queue's own job-type schema
// means the two can no longer drift apart.
export const controlPlaneProcessingStageKindSchema = processingJobTypeSchema;
/**
 * One pipeline stage's honest backlog for a connection: how many jobs are
 * queued (`pending`), running (`processing`), completed inside the recent window
 * (a coarse throughput signal, never an ETA promise) and terminally failed.
 * Counts only — no error text, payload or identifier is carried, and a stage the
 * backlog view does not report is simply absent, never a fabricated zero.
 */
export const controlPlaneProcessingStageSchema = z
    .object({
    kind: controlPlaneProcessingStageKindSchema,
    pending: z.number().int().nonnegative(),
    processing: z.number().int().nonnegative(),
    recentCompleted: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
})
    .strict();
/**
 * The source a queued job is working on, as the panel draws it. `unattributed`
 * is the deliberate catch-all for work that belongs to the connection but to no
 * single source — connection-wide pipeline stages, and anything the read surface
 * could not resolve — so a per-source breakdown can never quietly lose jobs.
 */
export const controlPlaneProcessingSourceKindSchema = z.enum([
    ...controlPlaneSourceKindSchema.options,
    "unattributed",
]);
/**
 * One source's share of a connection's backlog. Together with every other entry
 * these re-sum to the connection's own queued/in-progress/failed totals.
 */
export const controlPlaneProcessingSourceSchema = z
    .object({ kind: controlPlaneProcessingSourceKindSchema })
    .extend(controlPlaneSourceProcessingSchema.shape)
    .strict();
/**
 * The primary connection's processing backlog, aggregated from the worker queue
 * behind a safe read surface. `asOf` is the read timestamp so the panel can say
 * "as of {time}". When the read surface cannot be derived the whole field is
 * absent — an explicit "unknown" that keeps the panel honest instead of showing
 * a fabricated or global number.
 */
export const controlPlaneProcessingFlowSchema = z
    .object({
    stages: z.array(controlPlaneProcessingStageSchema).max(16),
    // The same backlog split by source instead of by pipeline stage, so the
    // panel can show each source row's own work. Additive and optional: absent
    // means only the stage breakdown could be read.
    sources: z.array(controlPlaneProcessingSourceSchema).max(16).optional(),
    asOf: dateTimeSchema,
    lastCompletedAt: dateTimeSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    const seen = new Set();
    value.stages.forEach((stage, index) => {
        if (seen.has(stage.kind)) {
            context.addIssue({
                code: "custom",
                path: ["stages", index, "kind"],
                message: "A processing stage cannot appear more than once.",
            });
        }
        seen.add(stage.kind);
    });
    const seenSources = new Set();
    value.sources?.forEach((source, index) => {
        if (seenSources.has(source.kind)) {
            context.addIssue({
                code: "custom",
                path: ["sources", index, "kind"],
                message: "A processing source cannot appear more than once.",
            });
        }
        seenSources.add(source.kind);
    });
});
/**
 * An unresolved operational blocker the member's own material is stuck behind:
 * a pipeline stage that stopped and has not produced a success since.
 *
 * It answers a question the backlog counts cannot. A queue depth of zero means
 * "nothing is waiting", which reads as healthy — and on 2026-07-28 it was the
 * reading while an entire lane was refusing every job. Blockers are derived
 * from the durable notification record instead, so an empty queue and a stopped
 * pipeline are distinguishable.
 *
 * Deliberately narrow: `kind` (does the member have to act, or is this ours to
 * fix) and `stage` (which part of the work) are what a surface needs to say
 * something true in plain language. The safe code that identifies the exact
 * failure stays in the database — it names an upstream provider, and no reader
 * needs it to be told that something is blocked.
 */
export const controlPlaneBlockerSchema = z
    .object({
    kind: z.enum(["reauthorization_required", "sync_permanently_failed"]),
    stage: processingJobTypeSchema,
    /** When this blocker was first recorded — never the read time. */
    since: dateTimeSchema,
})
    .strict();
/**
 * Contract 3 replaces the single nullable `connection` with a `connections`
 * array so the control plane can describe more than one provider connection per
 * member. Contract 2 (`controlPlaneSummarySchema`) stays byte-for-byte the same
 * for already-published clients. The top-level `state`, `coverage`,
 * `syncProgress`, and `issues` remain the aggregate view; with a single
 * connection they map one-to-one to the contract 2 payload.
 */
export const controlPlaneMultiConnectionSummarySchema = z
    .object({
    ...tenantOwnedShape,
    state: controlPlaneStateSchema,
    tenantName: nonEmptyStringSchema.max(300).nullable(),
    memberId: memberIdSchema,
    memberName: nonEmptyStringSchema.max(300).nullable(),
    connections: z.array(controlPlaneConnectionSchema).max(100),
    coverage: controlPlaneCoverageSchema,
    syncProgress: controlPlaneSyncProgressSchema.nullable().optional(),
    issues: z.array(controlPlaneIssueSchema).max(1000),
    // Additive, optional contract-3 field: the primary connection's processing
    // backlog. Absent means "not reported" — an honest unknown — so an
    // already-published strict client stays valid whether or not it is present.
    processingFlow: controlPlaneProcessingFlowSchema.optional(),
    // Additive, optional contract-3 field, and the same honest-absence rule: the
    // member's unresolved blockers. Absent means the record could not be read,
    // an empty array means it was read and nothing is blocked. Bounded at the
    // two kinds times the nine stages the record can distinguish.
    blockers: z.array(controlPlaneBlockerSchema).max(18).optional(),
    updatedAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceSameTenant(value.tenantId, value.connections.map((connection, index) => ({
        tenantId: connection.tenantId,
        path: ["connections", index],
    })), context);
    if (value.state === "empty" && value.connections.length > 0) {
        context.addIssue({
            code: "custom",
            path: ["connections"],
            message: "Empty control-plane state cannot contain a connection.",
        });
    }
    if (value.state !== "empty" && value.connections.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["connections"],
            message: "Connected control-plane states require at least one connection.",
        });
    }
    if (value.state === "scope_required" &&
        !value.connections.some((connection) => connection.visibilityClass === null)) {
        context.addIssue({
            code: "custom",
            path: ["connections"],
            message: "scope_required requires an unresolved visibility choice.",
        });
    }
    if (value.state === "healthy" &&
        !isControlPlaneSourceSyncComplete(value.coverage)) {
        context.addIssue({
            code: "custom",
            path: ["coverage"],
            message: "Healthy state requires a completed source synchronization.",
        });
    }
});
/**
 * Projects a contract 2 summary onto the contract 3 shape. The primary
 * connection (contract 2's single `connection`, today the Microsoft mailbox)
 * leads the array; `additionalConnections` — other providers held by the same
 * member, e.g. a linked WhatsApp account — follow it.
 *
 * The top-level `state`, `coverage` and `syncProgress` stay the aggregate view
 * of the primary connection; additional connections carry their own honest
 * per-connection fields and are read on their own terms. They are appended only
 * when a primary connection exists: the empty state forbids any connection, so
 * a provider-only-secondary tenant (no primary) is a deliberately deferred case
 * and yields an empty array here rather than an invariant violation.
 */
export function toControlPlaneMultiConnectionSummary(summary, additionalConnections = [], processingFlow = null, blockers = null) {
    const { connection, ...rest } = summary;
    const connections = connection
        ? [
            withSourceProcessing(connection, processingFlow),
            ...additionalConnections,
        ]
        : [];
    return controlPlaneMultiConnectionSummarySchema.parse({
        ...rest,
        connections,
        // Omit the key entirely when there is no flow, so an absent backlog reads as
        // an honest "unknown" rather than an empty-but-present object.
        ...(processingFlow ? { processingFlow } : {}),
        // Same rule, and the distinction matters more here: a present empty array is
        // "nothing is blocked", an absent field is "we could not tell".
        ...(blockers ? { blockers } : {}),
    });
}
/**
 * Folds the primary connection's source-attributed backlog into the source rows
 * it belongs to, so the panel shows the work where it is happening instead of in
 * a separate card. A drawn source with no entry becomes an explicit zero — the
 * surface was read, so "nothing outstanding" is a fact rather than an unknown —
 * while everything the connection draws no row for, including the read
 * surface's own unattributed bucket, is summed onto `processingUnattributed` so
 * the parts still equal the whole. The whole distribution is skipped when no
 * source breakdown was read, leaving both fields absent.
 */
function withSourceProcessing(connection, processingFlow) {
    const sources = processingFlow?.sources;
    if (!sources)
        return connection;
    const bySource = new Map(sources.map((source) => [source.kind, source]));
    const drawn = new Set(connection.sourceTypes?.map((type) => type.kind) ?? []);
    return {
        ...connection,
        ...(connection.sourceTypes
            ? {
                sourceTypes: connection.sourceTypes.map((sourceType) => ({
                    ...sourceType,
                    processing: sourceProcessingOf(bySource.get(sourceType.kind)),
                })),
            }
            : {}),
        processingUnattributed: sources
            .filter((source) => !drawn.has(source.kind))
            .reduce((total, source) => ({
            queued: total.queued + source.queued,
            inProgress: total.inProgress + source.inProgress,
            failed: total.failed + source.failed,
        }), { queued: 0, inProgress: 0, failed: 0 }),
    };
}
function sourceProcessingOf(source) {
    return {
        queued: source?.queued ?? 0,
        inProgress: source?.inProgress ?? 0,
        failed: source?.failed ?? 0,
    };
}
//# sourceMappingURL=control-plane.js.map